import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Search,
  Package,
  MapPin,
  Phone,
  Filter,
  Loader2,
  ShoppingBag,
  Building2,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const facilityTypeLabels = {
  hospital: 'Hôpital',
  clinic: 'Clinique',
  pharmacy: 'Pharmacie',
  laboratory: 'Laboratoire',
  imaging_center: "Centre d'imagerie",
  health_center: 'Centre de santé'
};

const ProductsSearch = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedCity, setSelectedCity] = useState(searchParams.get('city') || '');
  const [selectedFacilityType, setSelectedFacilityType] = useState(searchParams.get('type') || '');
  const [showFilters, setShowFilters] = useState(false);
  
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [ordering, setOrdering] = useState(false);
  const [orderForm, setOrderForm] = useState({
    quantity: 1,
    notes: '',
    patient_phone: '',
    patient_name: ''
  });

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('query', searchQuery);
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedCity) params.append('city', selectedCity);
      if (selectedFacilityType) params.append('facility_type', selectedFacilityType);
      params.append('page', page.toString());
      params.append('limit', '20');
      
      const response = await api.get(`/products/search?${params.toString()}`);
      setProducts(response.data.products || []);
      setTotal(response.data.total || 0);
      setPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory, selectedCity, selectedFacilityType, page]);

  const loadCategories = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCity) params.append('city', selectedCity);
      if (selectedFacilityType) params.append('facility_type', selectedFacilityType);
      
      const response = await api.get(`/products/public/categories?${params.toString()}`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  }, [selectedCity, selectedFacilityType]);

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, [loadProducts, loadCategories]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadProducts();
  };

  const handleOrder = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error('Veuillez vous connecter pour commander');
      navigate('/login');
      return;
    }
    
    if (!orderForm.patient_phone) {
      toast.error('Veuillez entrer votre numéro de téléphone');
      return;
    }
    
    setOrdering(true);
    try {
      await api.post('/products/orders', {
        product_id: selectedProduct.id,
        quantity: orderForm.quantity,
        notes: orderForm.notes,
        patient_phone: orderForm.patient_phone,
        patient_name: orderForm.patient_name || user?.full_name
      });
      
      toast.success('Commande envoyée ! Paiement sur place.');
      setShowOrderModal(false);
      setSelectedProduct(null);
      setOrderForm({ quantity: 1, notes: '', patient_phone: '', patient_name: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la commande');
    } finally {
      setOrdering(false);
    }
  };

  const openOrderModal = (product) => {
    if (!isAuthenticated) {
      toast.error('Veuillez vous connecter pour commander');
      navigate('/login');
      return;
    }
    
    setSelectedProduct(product);
    setOrderForm({
      quantity: 1,
      notes: '',
      patient_phone: user?.phone || '',
      patient_name: user?.full_name || ''
    });
    setShowOrderModal(true);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setSelectedCity('');
    setSelectedFacilityType('');
    setPage(1);
  };

  const hasActiveFilters = searchQuery || selectedCategory || selectedCity || selectedFacilityType;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="products-search-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1A237E] to-[#3949AB] py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Catalogue Santé
          </h1>
          <p className="text-white/80 mb-6">
            Examens, médicaments et services de santé près de chez vous
          </p>
          
          {/* Search Form */}
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Rechercher un examen, médicament, service..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 bg-white"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" size="lg" className="bg-red-500 hover:bg-red-600 h-12">
              <Search className="w-5 h-5 mr-2" />
              Rechercher
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              size="lg"
              onClick={() => setShowFilters(!showFilters)}
              className="bg-white h-12"
            >
              <Filter className="w-5 h-5 mr-2" />
              Filtres
            </Button>
          </form>
          
          {/* Filters Panel */}
          {showFilters && (
            <div className="bg-white rounded-xl p-4 mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm text-slate-600">Catégorie</Label>
                <select
                  value={selectedCategory}
                  onChange={(e) => { setSelectedCategory(e.target.value); setPage(1); }}
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  data-testid="category-filter"
                >
                  <option value="">Toutes les catégories</option>
                  {categories.map(cat => (
                    <option key={cat.name} value={cat.name}>
                      {cat.name} ({cat.count})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-sm text-slate-600">Ville</Label>
                <Input
                  placeholder="Ex: Yaoundé"
                  value={selectedCity}
                  onChange={(e) => { setSelectedCity(e.target.value); setPage(1); }}
                  className="mt-1"
                  data-testid="city-filter"
                />
              </div>
              <div>
                <Label className="text-sm text-slate-600">Type d&apos;établissement</Label>
                <select
                  value={selectedFacilityType}
                  onChange={(e) => { setSelectedFacilityType(e.target.value); setPage(1); }}
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  data-testid="facility-type-filter"
                >
                  <option value="">Tous les types</option>
                  <option value="pharmacy">Pharmacie</option>
                  <option value="laboratory">Laboratoire</option>
                  <option value="imaging_center">Centre d&apos;imagerie</option>
                  <option value="clinic">Clinique</option>
                  <option value="hospital">Hôpital</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Results Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <p className="text-slate-600">
              <span className="font-semibold">{total}</span> résultat(s)
            </p>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-red-500">
                <X className="w-4 h-4 mr-1" />
                Effacer les filtres
              </Button>
            )}
          </div>
        </div>

        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-red-500" />
          </div>
        ) : products.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600">Aucun résultat</h3>
              <p className="text-slate-500 text-sm mt-1">
                Essayez de modifier vos critères de recherche
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Products Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((product) => (
                <Card key={product.id} className="hover:shadow-lg transition-shadow" data-testid={`product-card-${product.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900 line-clamp-2">{product.name}</h3>
                        <Badge variant="outline" className="mt-1 text-xs">
                          {product.category}
                        </Badge>
                      </div>
                      <Badge className="bg-green-100 text-green-700 shrink-0 ml-2">
                        Disponible
                      </Badge>
                    </div>
                    
                    {product.description && (
                      <p className="text-sm text-slate-500 line-clamp-2 mb-3">
                        {product.description}
                      </p>
                    )}
                    
                    {/* Facility Info */}
                    {product.facility && (
                      <div className="bg-slate-50 rounded-lg p-3 mb-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          <span className="font-medium text-sm">{product.facility.name}</span>
                        </div>
                        {product.facility.address && (
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <MapPin className="w-3 h-3" />
                            <span className="line-clamp-1">{product.facility.address}</span>
                          </div>
                        )}
                        {product.facility.phone && (
                          <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                            <Phone className="w-3 h-3" />
                            <span>{product.facility.phone}</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between pt-3 border-t">
                      <p className="text-xl font-bold text-red-600">
                        {product.price.toLocaleString()} <span className="text-sm font-normal">FCFA</span>
                      </p>
                      <Button
                        size="sm"
                        onClick={() => openOrderModal(product)}
                        className="bg-red-500 hover:bg-red-600"
                        data-testid={`order-btn-${product.id}`}
                      >
                        <ShoppingBag className="w-4 h-4 mr-1" />
                        Commander
                      </Button>
                    </div>
                    
                    <p className="text-xs text-center text-slate-400 mt-2">
                      Paiement sur place
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="px-4 py-2 text-sm">
                  Page {page} sur {pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(pages, p + 1))}
                  disabled={page === pages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Order Modal */}
      <Dialog open={showOrderModal} onOpenChange={setShowOrderModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Commander</DialogTitle>
            <DialogDescription>
              Réservez ce produit/service - Paiement sur place
            </DialogDescription>
          </DialogHeader>

          {selectedProduct && (
            <form onSubmit={handleOrder} className="space-y-4 mt-4">
              {/* Product Summary */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold">{selectedProduct.name}</h3>
                <p className="text-sm text-slate-500">{selectedProduct.facility?.name}</p>
                <p className="text-lg font-bold text-red-600 mt-2">
                  {selectedProduct.price.toLocaleString()} FCFA
                </p>
              </div>

              <div>
                <Label>Votre nom</Label>
                <Input
                  value={orderForm.patient_name}
                  onChange={(e) => setOrderForm({ ...orderForm, patient_name: e.target.value })}
                  placeholder="Votre nom complet"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Téléphone *</Label>
                <Input
                  value={orderForm.patient_phone}
                  onChange={(e) => setOrderForm({ ...orderForm, patient_phone: e.target.value })}
                  placeholder="Ex: 6 XX XX XX XX"
                  className="mt-1"
                  required
                  data-testid="order-phone-input"
                />
              </div>

              <div>
                <Label>Quantité</Label>
                <Input
                  type="number"
                  value={orderForm.quantity}
                  onChange={(e) => setOrderForm({ ...orderForm, quantity: parseInt(e.target.value) || 1 })}
                  min="1"
                  max="10"
                  className="mt-1 w-24"
                />
              </div>

              <div>
                <Label>Notes (optionnel)</Label>
                <Textarea
                  value={orderForm.notes}
                  onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })}
                  placeholder="Précisions, questions..."
                  className="mt-1"
                  rows={2}
                />
              </div>

              {/* Total */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Total à payer sur place</span>
                  <span className="text-xl font-bold text-green-700">
                    {(selectedProduct.price * orderForm.quantity).toLocaleString()} FCFA
                  </span>
                </div>
                <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Paiement en espèces ou Mobile Money à l&apos;établissement
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowOrderModal(false)}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={ordering || !orderForm.patient_phone}
                  className="flex-1 bg-red-500 hover:bg-red-600"
                  data-testid="confirm-order-btn"
                >
                  {ordering ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <ShoppingBag className="w-4 h-4 mr-2" />
                      Confirmer
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsSearch;
